import { ChevronRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import ExportButtons from '../../../components/ExportButtons'
import SearchBar from '../../../components/SearchBar'
import type { ColumnDefinition, ExportMetadata } from '../../../utils/exportToCsv'
import type { StaatidMerkeRow } from './api'
import { formatDager } from './api'

interface Props {
  rows: StaatidMerkeRow[]
  total: number
  /** Opens the merke's detail page (#490) — replaces the old inline
   *  merke -> modell drilldown, whose models now live on that page. */
  onMerkeClick: (merke: string) => void
  /** The Modeller tab (#695): one row per (merke, modell), with a Modell
   *  column, and a click opens that model's page. */
  visModell?: boolean
  onModellClick?: (merke: string, modell: string) => void
  offset: number
  limit: number
  onPageChange: (offset: number) => void
  loading?: boolean
  exportMeta?: Partial<ExportMetadata>
  /** Server-side search (#695), as in Bruktbil → Merke & modell (#476). */
  search?: string
  onSearchChange?: (q: string) => void
}

// Built at render so the headers follow the UI language (#714).
const exportColumns = (t: TFunction<'staatid'>): ColumnDefinition[] => [
  { key: 'rang', label: t('common.rank') },
  { key: 'merke', label: t('common.merke') },
  { key: 'modell', label: t('common.modell') },
  { key: 'antall', label: t('common.antall'), format: (v: number) => v.toLocaleString('nb-NO') },
  { key: 'median_dager', label: t('common.medianDager'), format: (v: number | null) => (v === null ? '-' : String(v)) },
  { key: 'snitt_dager', label: t('common.snittDager'), format: (v: number | null) => (v === null ? '-' : String(v)) },
]

export default function MerkerTable({
  rows, total, onMerkeClick, visModell = false, onModellClick, offset, limit, onPageChange,
  loading, exportMeta, search, onSearchChange,
}: Props) {
  const { t } = useTranslation('staatid')
  if (loading) {
    return (
      <div className="card p-4">
        <div data-testid="table-skeleton" className="animate-pulse h-64 bg-gray-100 dark:bg-dark-border rounded-sm" />
      </div>
    )
  }

  const exportRows = rows.map((row, i) => ({ rang: offset + i + 1, ...row }))
  const page = Math.floor(offset / limit) + 1
  const pages = Math.max(1, Math.ceil(total / limit))

  return (
    <div data-testid={visModell ? 'staatid-modeller-table' : 'staatid-merker-table'} className="card overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-dark-card">
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
          <span>{visModell
            ? t('merkerTable.titleModeller', { n: total.toLocaleString('nb-NO') })
            : t('merkerTable.titleMerker', { n: total.toLocaleString('nb-NO') })}</span>
        </span>
        <ExportButtons
          data={exportRows}
          columns={exportColumns(t)}
          metadata={{ title: visModell ? t('exportTitles.models') : t('exportTitles.brands'), ...exportMeta }}
        />
      </div>
      {onSearchChange && (
        <div className="px-4 py-2 border-b border-gray-200 dark:border-dark-border">
          <SearchBar
            value={search ?? ''}
            onChange={onSearchChange}
            placeholder={visModell ? t('merkerTable.searchMerkeModell') : t('merkerTable.searchMerke')}
          />
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-dark-card">
              <th className="px-4 py-2 text-left font-medium text-gray-600 dark:text-gray-400">{t('common.rank')}</th>
              <th className="px-4 py-2 text-left font-medium text-gray-600 dark:text-gray-400">{t('common.merke')}</th>
              {visModell && (
                <th className="px-4 py-2 text-left font-medium text-gray-600 dark:text-gray-400">{t('common.modell')}</th>
              )}
              <th className="px-4 py-2 text-right font-medium text-gray-600 dark:text-gray-400">{t('common.antall')}</th>
              <th className="px-4 py-2 text-right font-medium text-gray-600 dark:text-gray-400">{t('common.medianDager')}</th>
              <th className="px-4 py-2 text-right font-medium text-gray-600 dark:text-gray-400">{t('common.snittDager')}</th>
              <th className="px-4 py-2" />
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => {
              const name = row.merke
              return (
                <tr
                  key={`${row.merke}-${row.modell}-${i}`}
                  data-testid={visModell ? 'staatid-modeller-rad' : 'staatid-merke-rad'}
                  onClick={() => {
                    if (visModell) {
                      if (row.merke && row.modell) onModellClick?.(row.merke, row.modell)
                    } else if (row.merke) onMerkeClick(row.merke)
                  }}
                  className={`border-b border-gray-100 dark:border-dark-border transition-colors cursor-pointer hover:bg-gray-100 dark:hover:bg-dark-border ${i % 2 === 0 ? 'bg-gray-50 dark:bg-gray-800/30' : ''}`}
                >
                  <td className="px-4 py-2 text-gray-500 tabular-nums">{offset + i + 1}</td>
                  <td className="px-4 py-2">{name ?? '—'}</td>
                  {visModell && <td className="px-4 py-2">{row.modell ?? '—'}</td>}
                  <td className="px-4 py-2 text-right tabular-nums">{row.antall.toLocaleString('nb-NO')}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{formatDager(row.median_dager)}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{formatDager(row.snitt_dager)}</td>
                  <td className="px-4 py-2 text-right">
                    <span className="inline-flex items-center gap-1 text-primary-600 dark:text-primary-400">
                      <span className="text-xs font-medium">{t('merkerTable.seDetaljer')}</span>
                      <ChevronRight className="w-4 h-4" />
                    </span>
                  </td>
                </tr>
              )
            })}
            {rows.length === 0 && (
              <tr>
                <td colSpan={visModell ? 7 : 6} className="px-4 py-6 text-center text-gray-400">{t('common.noData')}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {total > limit && (
        <div className="flex items-center justify-between px-4 py-2 border-t border-gray-200 dark:border-dark-border text-sm">
          <span className="text-gray-500 dark:text-gray-400">{t('common.pageOf', { page, pages })}</span>
          <div className="flex gap-2">
            <button
              className="btn-secondary text-xs px-3 py-1 disabled:opacity-40"
              disabled={offset === 0}
              onClick={() => onPageChange(Math.max(0, offset - limit))}
            >
              {t('common.prev')}
            </button>
            <button
              className="btn-secondary text-xs px-3 py-1 disabled:opacity-40"
              disabled={offset + limit >= total}
              onClick={() => onPageChange(offset + limit)}
            >
              {t('common.next')}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
